import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

// Move createPath outside component to prevent recreation on every render
// Jeremy's feedback: "Functions like createPath don't use outside variables, so move them outside"
const createPath = (startAngle: number, endAngle: number, isHovered: boolean = false) => {
  const centerX = 140;
  const centerY = 140;
  const outerRadius = isHovered ? 115 : 110;
  const innerRadius = 70;

  const startAngleRad = (startAngle * Math.PI) / 180;
  const endAngleRad = (endAngle * Math.PI) / 180;

  const x1 = centerX + outerRadius * Math.cos(startAngleRad);
  const y1 = centerY + outerRadius * Math.sin(startAngleRad);
  const x2 = centerX + outerRadius * Math.cos(endAngleRad);
  const y2 = centerY + outerRadius * Math.sin(endAngleRad);

  const x3 = centerX + innerRadius * Math.cos(endAngleRad);
  const y3 = centerY + innerRadius * Math.sin(endAngleRad);
  const x4 = centerX + innerRadius * Math.cos(startAngleRad);
  const y4 = centerY + innerRadius * Math.sin(startAngleRad);

  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  return `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4} Z`;
};

interface DonutChartProps {
  data: {
    green: number;
    yellow: number;
    red: number;
  };
  title: string;
}

const DonutChart = ({ data, title }: DonutChartProps) => {
  const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (segment: string) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    setHoveredSegment(segment);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredSegment(null);
    }, 100); // Small delay to prevent rapid flickering
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Keep all three segments separate
  const green = data.green;
  const yellow = data.yellow;
  const red = data.red;
  const total = green + yellow + red;

  // Calculate percentages for all three segments
  const greenPercentage = total > 0 ? (green / total) * 100 : 0;
  const yellowPercentage = total > 0 ? (yellow / total) * 100 : 0;
  const redPercentage = total > 0 ? (red / total) * 100 : 0;

  // Calculate angles for all three segments
  const greenAngle = total > 0 ? (green / total) * 360 : 0;
  const yellowAngle = total > 0 ? (yellow / total) * 360 : 0;
  const redAngle = total > 0 ? (red / total) * 360 : 0;

  // SVG path creation function moved outside component for better performance

  let currentAngle = -90; // Start from top

  // Create paths for all three segments
  const greenPath =
    green > 0
      ? createPath(currentAngle, currentAngle + greenAngle, hoveredSegment === 'green')
      : '';
  currentAngle += greenAngle;

  const yellowPath =
    yellow > 0
      ? createPath(currentAngle, currentAngle + yellowAngle, hoveredSegment === 'yellow')
      : '';
  currentAngle += yellowAngle;

  const redPath =
    red > 0 ? createPath(currentAngle, currentAngle + redAngle, hoveredSegment === 'red') : '';

  // Calculate tooltip position based on segment center
  const getTooltipPosition = (segment: string) => {
    let segmentStartAngle = -90; // Start from top
    let segmentAngle = 0;

    if (segment === 'green') {
      segmentStartAngle = -90;
      segmentAngle = greenAngle;
    } else if (segment === 'yellow') {
      segmentStartAngle = -90 + greenAngle;
      segmentAngle = yellowAngle;
    } else if (segment === 'red') {
      segmentStartAngle = -90 + greenAngle + yellowAngle;
      segmentAngle = redAngle;
    }

    // Calculate the middle angle of the segment
    const middleAngle = segmentStartAngle + segmentAngle / 2;
    const angleRad = (middleAngle * Math.PI) / 180;

    // Position tooltip at a distance from the center (outside the chart)
    const distance = 160; // Distance from center
    const centerX = 140; // SVG center X
    const centerY = 140; // SVG center Y

    const tooltipX = centerX + distance * Math.cos(angleRad);
    const tooltipY = centerY + distance * Math.sin(angleRad);

    return { x: tooltipX, y: tooltipY };
  };

  const tooltipPosition = hoveredSegment ? getTooltipPosition(hoveredSegment) : { x: 0, y: 0 };

  return (
    <motion.div
      className="flex flex-col items-center"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <motion.h3
        className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent mb-6 text-center"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
      >
        {title}
      </motion.h3>

      <div className="relative group z-40">
        <motion.svg
          width="280"
          height="280"
          viewBox="0 0 280 280"
          className="transform transition-all duration-500 group-hover:scale-105"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, duration: 0.7, ease: 'easeOut' }}
        >
          {/* Background circle for shadow effect */}
          <circle
            cx="140"
            cy="140"
            r="110"
            fill="none"
            stroke="rgba(0,0,0,0.05)"
            strokeWidth="2"
            className="opacity-50"
          />

          {/* Green segment */}
          {green > 0 && (
            <motion.path
              d={greenPath}
              fill="url(#greenGradient)"
              className="cursor-pointer filter drop-shadow-lg"
              onMouseEnter={() => handleMouseEnter('green')}
              onMouseLeave={handleMouseLeave}
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 0.5, duration: 1.2, ease: 'easeInOut' }}
              whileHover={{
                filter: 'brightness(1.1) drop-shadow(0 8px 16px rgba(16, 185, 129, 0.3))',
                transition: { duration: 0.2 },
              }}
            />
          )}

          {/* Yellow segment */}
          {yellow > 0 && (
            <motion.path
              d={yellowPath}
              fill="url(#yellowGradient)"
              className="cursor-pointer filter drop-shadow-lg"
              onMouseEnter={() => handleMouseEnter('yellow')}
              onMouseLeave={handleMouseLeave}
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 0.6, duration: 1.2, ease: 'easeInOut' }}
              whileHover={{
                filter: 'brightness(1.1) drop-shadow(0 8px 16px rgba(245, 158, 11, 0.3))',
                transition: { duration: 0.2 },
              }}
            />
          )}

          {/* Red segment */}
          {red > 0 && (
            <motion.path
              d={redPath}
              fill="url(#redGradient)"
              className="cursor-pointer filter drop-shadow-lg"
              onMouseEnter={() => handleMouseEnter('red')}
              onMouseLeave={handleMouseLeave}
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 0.7, duration: 1.2, ease: 'easeInOut' }}
              whileHover={{
                filter: 'brightness(1.1) drop-shadow(0 8px 16px rgba(239, 68, 68, 0.3))',
                transition: { duration: 0.2 },
              }}
            />
          )}

          {/* Gradient definitions */}
          <defs>
            <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#34D399" />
              <stop offset="50%" stopColor="#10B981" />
              <stop offset="100%" stopColor="#059669" />
            </linearGradient>
            <linearGradient id="yellowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#FBBF24" />
              <stop offset="50%" stopColor="#F59E0B" />
              <stop offset="100%" stopColor="#D97706" />
            </linearGradient>
            <linearGradient id="redGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F87171" />
              <stop offset="50%" stopColor="#EF4444" />
              <stop offset="100%" stopColor="#DC2626" />
            </linearGradient>
          </defs>

          {/* Center text */}
          <motion.text
            x="140"
            y="120"
            textAnchor="middle"
            className="fill-gray-500 dark:fill-gray-400 text-xs font-semibold tracking-widest"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.4 }}
          >
            TOTAL
          </motion.text>
          <motion.text
            x="140"
            y="165"
            textAnchor="middle"
            className="fill-gray-900 dark:fill-white text-3xl font-black"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.4, duration: 0.6, ease: 'backOut' }}
          >
            {total.toLocaleString()}
          </motion.text>
        </motion.svg>

        {/* Dynamic positioned tooltip based on segment */}
        <AnimatePresence>
          {hoveredSegment && (
            <motion.div
              className="absolute bg-gradient-to-br from-white/95 to-gray-50/95 dark:from-gray-800/95 dark:to-gray-900/95 text-gray-800 dark:text-white px-3 py-2 rounded-2xl shadow-xl border border-white/50 dark:border-gray-700/50 backdrop-blur-md pointer-events-none transform -translate-x-1/2 -translate-y-1/2 ring-1 ring-black/5 dark:ring-white/10 z-50"
              style={{
                left: `${tooltipPosition.x}px`,
                top: `${tooltipPosition.y}px`,
                minWidth: '120px',
                maxWidth: '140px',
                boxShadow:
                  '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(255, 255, 255, 0.1)',
              }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              {/* Compact content */}
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      hoveredSegment === 'green'
                        ? 'bg-green-500'
                        : hoveredSegment === 'yellow'
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                    }`}
                  />
                  <div className="text-xs font-semibold">
                    {hoveredSegment === 'green' && 'Healthy'}
                    {hoveredSegment === 'yellow' && 'Borderline'}
                    {hoveredSegment === 'red' && 'Disengaged'}
                  </div>
                </div>
                <div className="text-lg font-bold mb-0.5">
                  {hoveredSegment === 'green' && green.toLocaleString()}
                  {hoveredSegment === 'yellow' && yellow.toLocaleString()}
                  {hoveredSegment === 'red' && red.toLocaleString()}
                </div>
                <div className="text-xs opacity-70 font-medium">
                  {hoveredSegment === 'green' && `${greenPercentage.toFixed(1)}%`}
                  {hoveredSegment === 'yellow' && `${yellowPercentage.toFixed(1)}%`}
                  {hoveredSegment === 'red' && `${redPercentage.toFixed(1)}%`}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Enhanced Legend */}
      <motion.div
        className="mt-6 flex justify-center items-center gap-3 flex-wrap"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6, duration: 0.4 }}
      >
        {green > 0 && (
          <motion.div
            className="flex items-center bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-3 py-2 rounded-xl border border-green-200/50 dark:border-green-700/50 shadow-lg"
            whileHover={{ scale: 1.05, y: -2 }}
            transition={{ duration: 0.2 }}
          >
            <div className="w-4 h-4 rounded-full bg-gradient-to-br from-green-400 to-green-600 mr-2 shadow-lg flex-shrink-0"></div>
            <div className="text-xs">
              <div className="font-bold text-gray-900 dark:text-white">Healthy</div>
              <div className="text-gray-600 dark:text-gray-400 font-semibold">
                {green.toLocaleString()} ({greenPercentage.toFixed(1)}%)
              </div>
            </div>
          </motion.div>
        )}
        {yellow > 0 && (
          <motion.div
            className="flex items-center bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-3 py-2 rounded-xl border border-yellow-200/50 dark:border-yellow-700/50 shadow-lg"
            whileHover={{ scale: 1.05, y: -2 }}
            transition={{ duration: 0.2 }}
          >
            <div className="w-4 h-4 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 mr-2 shadow-lg flex-shrink-0"></div>
            <div className="text-xs">
              <div className="font-bold text-gray-900 dark:text-white">Borderline</div>
              <div className="text-gray-600 dark:text-gray-400 font-semibold">
                {yellow.toLocaleString()} ({yellowPercentage.toFixed(1)}%)
              </div>
            </div>
          </motion.div>
        )}
        {red > 0 && (
          <motion.div
            className="flex items-center bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-3 py-2 rounded-xl border border-red-200/50 dark:border-red-700/50 shadow-lg"
            whileHover={{ scale: 1.05, y: -2 }}
            transition={{ duration: 0.2 }}
          >
            <div className="w-4 h-4 rounded-full bg-gradient-to-br from-red-400 to-red-600 mr-2 shadow-lg flex-shrink-0"></div>
            <div className="text-xs">
              <div className="font-bold text-gray-900 dark:text-white">Disengaged</div>
              <div className="text-gray-600 dark:text-gray-400 font-semibold">
                {red.toLocaleString()} ({redPercentage.toFixed(1)}%)
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default DonutChart;
